import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { arrow, flip, FloatingArrow, offset, useFloating, useFocus, useHover, useInteractions, useRole, } from '@floating-ui/react';
import { useContext, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { extendTailwindMerge } from 'tailwind-merge';
import { LocaleContext, isRtl } from '../../../i18n/src';
import { getDirectionAwarePlacement } from './utils/get_direction_aware_placement';
const twMerge = extendTailwindMerge({
    prefix: 'nucleus-',
});
const defaultPlacement = 'bottom';
/**
 * A tooltip is a popup that displays information related to an element when the element receives keyboard focus, the
 * mouse hovers over it or a touch device user taps on it. A keyboard user can also press the 'Escape' key to remove
 * element focus.
 *
 * @remarks
 *
 * Design guidelines dictate that tooltips should be used sparingly and that text should be kept short to at most 3
 *   lines.
 */
export const Tooltip = ({ as: WrapperElement = 'button', children, className, delayHide = 0, delayShow = 0, disabled = false, displayOnlyOnOverflow = false, placement = defaultPlacement, title, wrapperClassName, }) => {
    const locale = useContext(LocaleContext);
    const isLtr = !isRtl(locale);
    const directionAwarePlacement = getDirectionAwarePlacement({ placement, isLtr });
    const [isOpen, setIsOpen] = useState(false);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context, elements } = useFloating({
        placement: directionAwarePlacement,
        middleware: [offset(6), flip({ crossAxis: false }), arrow({ element: arrowRef })],
        open: isOpen,
        onOpenChange: setIsOpen,
    });
    const role = useRole(context, { role: 'tooltip' });
    const hover = useHover(context, { delay: { open: delayShow, close: delayHide } });
    const focus = useFocus(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([role, hover, focus]);
    const tooltipId = `tooltip-${`${title}`.toLowerCase().replace(/\s+/g, '-')}`;
    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.currentTarget.blur();
        }
    };
    const isOverflowing = elements.domReference && elements.domReference.scrollWidth > elements.domReference.clientWidth;
    return (_jsxs(_Fragment, { children: [_jsx(WrapperElement, Object.assign({ ref: refs.setReference, onKeyDown: handleKeyDown, className: twMerge('nucleus-box-border nucleus-inline-flex nucleus-cursor-pointer nucleus-bg-transparent nucleus-font-sans nucleus-text-base nucleus-text-text-primary nucleus-normal-case nucleus-m-0 nucleus-border-0 focus-visible:nucleus-outline focus-visible:nucleus-outline-2 focus-visible:nucleus-outline-offset-4 focus-visible:nucleus-outline-primary-500', wrapperClassName), "aria-describedby": isOpen ? tooltipId : undefined }, getReferenceProps(), { children: children })), isOpen &&
                !disabled &&
                (!displayOnlyOnOverflow || isOverflowing) &&
                createPortal(_jsxs("div", Object.assign({ role: 'tooltip', id: tooltipId, ref: refs.setFloating }, getFloatingProps(), { className: `
              nucleus-absolute nucleus-pointer-events-none nucleus-z-50 nucleus-max-w-tooltip nucleus-animate-fadeIn nucleus-bg-neutral-700 dark:nucleus-bg-neutral-300 
              nucleus-font-sans nucleus-text-xs nucleus-font-normal nucleus-text-white nucleus-shadow-lg nucleus-rounded-lg nucleus-p-3 
              ${className}
           `, style: floatingStyles, children: [_jsx(FloatingArrow, { ref: arrowRef, context: context, className: 'nucleus-fill-neutral-700 dark:nucleus-fill-neutral-300' }), title] })), document.body)] }));
};
//# sourceMappingURL=tooltip.js.map